import type { StateCreator } from 'zustand';
import type { AppStore } from './types';
import type { BiomeId, ItemType, WorldState } from '@/domain/world/types';
import { emptyWorldState, INVENTORY_MAX_PER_TYPE } from '@/domain/world/types';
import type { Tree, WorldEvent } from '@/domain/world/evolve';
import type { Memorial } from '@/domain/world/memorial';
import type { AfkDispatch } from '@/domain/world/afk';
import type { WildBestiary } from '@/domain/world/wild-bestiary';

export interface WorldSliceState {
  world: WorldState;
  inWorld: boolean;
  /** Posição do player no mundo, em pixel coords. */
  playerX: number;
  playerY: number;
}

export interface WorldSliceActions {
  setWorld: (w: WorldState) => void;
  setInWorld: (b: boolean) => void;
  setPlayerPos: (x: number, y: number) => void;
  addToInventory: (type: ItemType, amount: number) => void;
  markItemCollected: (biome: BiomeId, itemId: string) => void;
  discoverRegion: (biome: BiomeId) => void;
  setLastBiome: (biome: BiomeId) => void;
  setRegionItems: (biome: BiomeId, items: WorldState['regionItems'][BiomeId]) => void;
  setDayCount: (n: number) => void;
  // U6
  addTree: (t: Tree) => void;
  updateTree: (id: string, patch: Partial<Tree>) => void;
  setTrees: (trees: Tree[]) => void;
  addMemorial: (m: Memorial) => void;
  setMemorials: (m: Memorial[]) => void;
  addEvent: (e: WorldEvent) => void;
  recordEnemyKill: (enemyId: string) => void;
  setEnemyKills: (k: Record<string, number>) => void;
  setWildBestiary: (bestiary: WildBestiary) => void;
  addAfkDispatch: (d: AfkDispatch) => void;
  updateAfkDispatch: (id: string, patch: Partial<AfkDispatch>) => void;
  removeAfkDispatch: (id: string) => void;
  setAfkDispatches: (d: AfkDispatch[]) => void;
  incrementGeneration: () => void;
  setGeneration: (n: number) => void;
}

export type WorldSlice = WorldSliceState & WorldSliceActions;

export const createWorldSlice: StateCreator<AppStore, [], [], WorldSlice> = (set) => ({
  world: emptyWorldState(),
  inWorld: false,
  playerX: 180,
  playerY: 800,
  setWorld: (world) => set({ world }),
  setInWorld: (b) => set({ inWorld: b }),
  setPlayerPos: (x, y) => set({ playerX: x, playerY: y }),
  addToInventory: (type, amount) =>
    set((s) => ({
      world: {
        ...s.world,
        inventory: {
          ...s.world.inventory,
          [type]: Math.max(
            0,
            Math.min(INVENTORY_MAX_PER_TYPE, s.world.inventory[type] + amount),
          ),
        },
      },
    })),
  markItemCollected: (biome, itemId) =>
    set((s) => ({
      world: {
        ...s.world,
        regionItems: {
          ...s.world.regionItems,
          [biome]: s.world.regionItems[biome].map((it) =>
            it.id === itemId ? { ...it, collected: true } : it,
          ),
        },
      },
    })),
  discoverRegion: (biome) =>
    set((s) => {
      if (s.world.regionsDiscovered.includes(biome)) return s;
      return {
        world: { ...s.world, regionsDiscovered: [...s.world.regionsDiscovered, biome] },
      };
    }),
  setLastBiome: (biome) =>
    set((s) => ({ world: { ...s.world, lastBiome: biome } })),
  setRegionItems: (biome, items) =>
    set((s) => ({
      world: {
        ...s.world,
        regionItems: { ...s.world.regionItems, [biome]: items },
      },
    })),
  setDayCount: (n) => set((s) => ({ world: { ...s.world, dayCount: n } })),
  addTree: (t) => set((s) => ({ world: { ...s.world, trees: [...(s.world.trees ?? []), t] } })),
  updateTree: (id, patch) =>
    set((s) => ({
      world: {
        ...s.world,
        trees: (s.world.trees ?? []).map((t) => (t.id === id ? { ...t, ...patch } : t)),
      },
    })),
  setTrees: (trees) => set((s) => ({ world: { ...s.world, trees } })),
  addMemorial: (m) =>
    set((s) => {
      const existing = s.world.memorials ?? [];
      if (existing.some((x) => x.id === m.id)) return s;
      return { world: { ...s.world, memorials: [...existing, m] } };
    }),
  setMemorials: (m) => set((s) => ({ world: { ...s.world, memorials: m } })),
  addEvent: (e) => set((s) => ({ world: { ...s.world, events: [...(s.world.events ?? []), e] } })),
  recordEnemyKill: (enemyId) =>
    set((s) => {
      const cur = s.world.enemyKills ?? {};
      return { world: { ...s.world, enemyKills: { ...cur, [enemyId]: (cur[enemyId] ?? 0) + 1 } } };
    }),
  setEnemyKills: (k) => set((s) => ({ world: { ...s.world, enemyKills: k } })),
  setWildBestiary: (wildBestiary) => set((s) => ({ world: { ...s.world, wildBestiary } })),
  addAfkDispatch: (d) =>
    set((s) => ({ world: { ...s.world, afkDispatches: [...(s.world.afkDispatches ?? []), d] } })),
  updateAfkDispatch: (id, patch) =>
    set((s) => ({
      world: {
        ...s.world,
        afkDispatches: (s.world.afkDispatches ?? []).map((d) =>
          d.id === id ? { ...d, ...patch } : d,
        ),
      },
    })),
  removeAfkDispatch: (id) =>
    set((s) => ({
      world: {
        ...s.world,
        afkDispatches: (s.world.afkDispatches ?? []).filter((d) => d.id !== id),
      },
    })),
  setAfkDispatches: (d) => set((s) => ({ world: { ...s.world, afkDispatches: d } })),
  incrementGeneration: () =>
    set((s) => ({ world: { ...s.world, generation: (s.world.generation ?? 0) + 1 } })),
  setGeneration: (n) => set((s) => ({ world: { ...s.world, generation: n } })),
});
